import React, { useState } from "react";

interface PropertyCardProps {
  property: {
    id: number;
    slug?: string;
    city: string;
    state: string;
    country?: string;
    image?: string;
  };
  defaultImage: string;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property, defaultImage }) => {
  // ─── Image state ────────────────────────────────────────────────────────
  // Root cause of the bug:
  //   `property?.image || defaultImage` treats "" (empty string) as falsy,
  //   so a property with image="" silently falls back to the default image.
  //
  // Fix: explicitly check that image is a non-empty string before using it.
  // We also track a per-card error state so a broken URL doesn't leak the
  // default image in as if it were intentional content.
  const hasValidImage =
    typeof property?.image === "string" && property.image.trim().length > 0;

  const [imgSrc, setImgSrc] = useState<string>(
    hasValidImage ? property.image! : "" // start empty — render placeholder if no image
  );
  const [imgError, setImgError] = useState<boolean>(!hasValidImage);

  const handleError = () => {
    // Only fall back to defaultImage if the original src was genuinely broken
    // (not if it was already the defaultImage to avoid an infinite error loop)
    if (imgSrc !== defaultImage) {
      setImgSrc(defaultImage);
      setImgError(true);
    }
  };

  const formatSlug = (slug: string) =>
    slug
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  const propertyTitle = property?.slug ? formatSlug(property.slug) : "Property";
  const location = [property?.city, property?.state, property?.country]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="group relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden hover:shadow-[0_8px_30px_rgba(245,105,72,0.12)] hover:-translate-y-0.5 transition-all duration-200">

      {/* ── Image ──────────────────────────────────────────────────────── */}
      <div className="relative w-full h-44 bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
        {imgSrc && !imgError ? (
          <img
            src={imgSrc}
            alt={propertyTitle}
            onError={handleError}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : imgSrc && imgError && imgSrc === defaultImage ? (
          /* defaultImage loaded as fallback — show it but muted */
          <img
            src={defaultImage}
            alt={propertyTitle}
            className="w-full h-full object-cover opacity-60"
          />
        ) : (
          /* No image at all — branded placeholder */
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            <div className="w-12 h-12 rounded-xl bg-[#F56948]/10 flex items-center justify-center">
              <svg className="w-6 h-6 text-[#F56948]/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 22V12h6v10" />
              </svg>
            </div>
            <span className="text-xs text-neutral-400 font-medium">No image available</span>
          </div>
        )}

        {/* Orange accent bar at bottom of image */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#F56948] to-[#F56948]/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
      </div>

      {/* ── Content ────────────────────────────────────────────────────── */}
      <div className="p-4 flex flex-col gap-2">

        {/* Title */}
        <h4 className="text-sm font-bold text-[#1A1A2E] dark:text-white leading-snug line-clamp-2 tracking-tight">
          {propertyTitle}
        </h4>

        {/* Location */}
        <div className="flex items-start gap-1.5">
          <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#F56948]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-xs text-neutral-400 dark:text-neutral-500 line-clamp-1">
            {location}
          </span>
        </div>

        {/* Divider */}
        <div className="h-px bg-neutral-100 dark:bg-neutral-800 mt-1" />

        {/* View link */}
        {property?.slug && (
          <a
            href={`/listing-stay-detail/${property.slug}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F56948] hover:text-[#e0522f] transition-colors mt-0.5"
          >
            View listing
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        )}
      </div>
    </div>
  );
};

export default PropertyCard;